import { RingFinishEnum } from '@jouzen/ecom-components/v3/enums/ringFinish';
import { type FocusEvent, useRef, useState } from 'react';
import { useFocusWithin } from 'react-aria';

import {
  PDP_NEW_PREPAID_INTERSTITIAL,
  PDP_PREPAID_INTERSTITIAL,
} from '@/app/[locale]/product/_components/HardwarePDP/_consts/pdpVariants';
import { useScroller } from '@/app/[locale]/product/_hooks/useScroller';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import SizeSelection from '@/app/[locale]/product/_steps/enums/SizeStep';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';

import type { UseRingScroller } from './types';
import usePDPExperiment from './usePDPExperiment';

const baseSteps = {
  [RingFinishEnum.Silver]: 0,
  [RingFinishEnum.Black]: 0,
  [RingFinishEnum.Stealth]: 0,
  [RingFinishEnum.BrushedSilver]: 0,
  [RingFinishEnum.Gold]: 0,
  [RingFinishEnum.RoseGold]: 0,
  [RingFinishEnum.Midnight]: 0,
  [RingFinishEnum.Cloud]: 0,
  [RingFinishEnum.Tide]: 0,
  [RingFinishEnum.Petal]: 0,
  [SizeSelection.SizingKit]: 1,
  [SizeSelection.ChooseSize]: 1,
};

const currentStep = {
  ...baseSteps,
  [MembershipSelection.OneMonth]: 2,
  [MembershipSelection.OneYear]: 2,
  [MembershipSelection.CurrentMember]: 2,
  [WarrantySelection.OneYear]: 3,
  [WarrantySelection.TwoYear]: 3,
  [WarrantySelection.ThreeYear]: 3,
};

const warrantySelectionVariantStep = {
  ...baseSteps,
  [WarrantySelection.OneYear]: 2,
  [WarrantySelection.TwoYear]: 2,
  [WarrantySelection.ThreeYear]: 2,
};

const useRingScroller = (): UseRingScroller => {
  const [currentStepId, setCurrentStepId] = useState<number>(0);
  const prevStepId = useRef<number>(0);
  const variant = usePDPExperiment();
  const _currentStep =
    variant === PDP_PREPAID_INTERSTITIAL ||
    variant === PDP_NEW_PREPAID_INTERSTITIAL
      ? warrantySelectionVariantStep
      : currentStep;

  const onFocusWithin = (e: FocusEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const target = e.target as HTMLInputElement;
    if (target.value in _currentStep) {
      setCurrentStepId(_currentStep[target.value as keyof typeof _currentStep]);
    }
  };

  const { focusWithinProps } = useFocusWithin({
    onFocusWithin,
  });

  const { sectionRefs } = useScroller(currentStepId, prevStepId);

  return { focusWithinProps, sectionRefs };
};

export default useRingScroller;
